import React, { useState } from "react";
import { useForm } from "react-hook-form";
import * as common from "../../util/common";
import { useSelector } from "react-redux";
import { ApplicationState } from "../../store";
import { PATTERNS } from "../../util/patterns";

const CompleteFamilySetupStep1 = (props: { firstName: string; lastName: string; setState: any; state: any; dob: string }) => {
    const isServiceActive = (serviceDisplayName: string, state: ApplicationState) => {
        const services =
            state.loggedInUser && state.loggedInUser.userServiceDetails && state.loggedInUser.userServiceDetails.userServices
                ? state.loggedInUser && state.loggedInUser.userServiceDetails.userServices
                : [];
        const selectedService = services.find((service) => service.displayName === serviceDisplayName);
        if (selectedService && selectedService.active) {
            return true;
        }
        return false;
    };

    const countryRequiresAuthentication = useSelector((state: ApplicationState) => {
        const country = state.myAccountServiceState?._unifiedAccountInfo?.country;
        return ['US', 'CA'].includes(country || '');
    });

    const [state, setState] = useState({
        firstName: props.firstName,
        lastName: props.lastName,
        dob: props.dob,
        middleName: props.state.middleName ? props.state.middleName : "",
        SSN: props.state.SSN ? props.state.SSN : "",
        confirmSSN: props.state.confirmSSN ? props.state.confirmSSN : "",
        ssn1: props.state.SSN ? props.state.SSN.substring(0, 3) : "",
        ssn2: props.state.SSN ? props.state.SSN.substring(3, 5) : "",
        ssn3: props.state.SSN ? props.state.SSN.substring(5) : "",
        cssn1: props.state.confirmSSN ? props.state.confirmSSN.substring(0, 3) : "",
        cssn2: props.state.confirmSSN ? props.state.confirmSSN.substring(3, 5) : "",
        cssn3: props.state.confirmSSN ? props.state.confirmSSN.substring(5) : "",
        confirmSNNError: false,
    });

    const [childAgeError, setChildAgeError] = useState(false);
    const [negativeAgeError, setNegativeError] = useState(false);

    const { register, handleSubmit, errors } = useForm({
        mode: "onSubmit",
        reValidateMode: "onChange",
    });

    const isIDFFamilyServiceActive = useSelector((state: ApplicationState) => isServiceActive("Family", state));
    const isIDFFamilyChildActive = useSelector((state: ApplicationState) => isServiceActive("Family 3.0 Child", state));
    const isChildWatchServiceActive = useSelector((state: ApplicationState) => isServiceActive("ChildWatch", state));

    const maximumChildAge = isIDFFamilyServiceActive ? 17 : 26;

    const getSSNForSubmission = () => {
        if (!countryRequiresAuthentication) {
            return "000000000";
        }
        return state.SSN;
    };

    const firstStepSubmit = (data: any) => {
        const isSSNRequired = countryRequiresAuthentication;

        const isSSNMatchWithConfirmSSN = isSSNRequired
        ? state.SSN === state.confirmSSN
        : true;
        const isChildAgeValid = !childAgeError && !negativeAgeError;
        const isValidData = isSSNMatchWithConfirmSSN && isChildAgeValid;
        if (isValidData) {
            props.setState({
                ...props.state,
                firstName: data.firstName,
                lastName: data.lastName,
                middleName: data.middleName,
                SSN: state.SSN,
                confirmSSN: getSSNForSubmission(),
                DOB: data.dob,
                emails: Boolean(props.state.emails) ? props.state.emails : [{ email: "" }],
                phone: Boolean(props.state.phone) ? props.state.phone : [{ phone: "", code: "+1" }],
                medicalId: props.state.medicalId ? props.state.medicalId : "",
                step: 2,
            });
        }
    };

    const handleSSN1 = (event: any) => {
        const ssn1 = event.target.value;
        const SSN = ssn1 + state.ssn2 + state.ssn3;
        const confirmSNNError = state.confirmSSN && state.confirmSSN !== SSN ? true : false;

        setState({
            ...state,
            ssn1,
            SSN,
            confirmSNNError,
        });

        if (ssn1.length > 2) {
            document.getElementById("ssn2")?.focus();
        }
    };

    const handleSSN2 = (event: any) => {
        const ssn2 = event.target.value;
        const SSN = state.ssn1 + ssn2 + state.ssn3;
        const confirmSNNError = state.confirmSSN && state.confirmSSN !== SSN ? true : false;

        setState({
            ...state,
            ssn2,
            SSN,
            confirmSNNError,
        });

        if (ssn2.length > 1) {
            document.getElementById("ssn3")?.focus();
        }
    };

    const handleSSN3 = (event: any) => {
        const ssn3 = event.target.value;
        const SSN = state.ssn1 + state.ssn2 + ssn3;
        const confirmSNNError = state.confirmSSN && state.confirmSSN !== SSN ? true : false;

        setState({
            ...state,
            ssn3,
            SSN,
            confirmSNNError,
        });

        if (ssn3.length > 3) {
            document.getElementById("cssn1")?.focus();
        }
    };

    const handleCSSN1 = (event: any) => {
        const cssn1 = event.target.value;
        const confirmSSN = cssn1 + state.cssn2 + state.cssn3;
        const confirmSNNError = confirmSSN !== state.SSN ? true : false;

        setState({
            ...state,
            cssn1,
            confirmSSN,
            confirmSNNError,
        });

        if (cssn1.length > 2) {
            document.getElementById("cssn2")?.focus();
        }
    };

    const handleCSSN2 = (event: any) => {
        const cssn2 = event.target.value;
        const confirmSSN = state.cssn1 + cssn2 + state.cssn3;
        const confirmSNNError = confirmSSN !== state.SSN ? true : false;

        setState({
            ...state,
            cssn2,
            confirmSSN,
            confirmSNNError,
        });

        if (cssn2.length > 1) {
            document.getElementById("cssn3")?.focus();
        }
    };

    const handleCSSN3 = (event: any) => {
        const cssn3 = event.target.value;
        const confirmSSN = state.cssn1 + state.cssn2 + cssn3;
        const confirmSNNError = confirmSSN !== state.SSN ? true : false;

        setState({
            ...state,
            cssn3,
            confirmSSN,
            confirmSNNError,
        });
    };

    let birthDateFieldPressedKey: number;
    const updatBirthDatePressedKey = (e: any) => {
        birthDateFieldPressedKey = e.keyCode;
    };

    const checkMemberAge = (date: string) => {
        try {
            const accountDOBinDateFormate = new Date(date);
            const age = common.calculate_age(accountDOBinDateFormate);
            const todaysDate = new Date();

            if (isIDFFamilyServiceActive && age > maximumChildAge) {
                setChildAgeError(true);
            } else if ((isIDFFamilyChildActive || isChildWatchServiceActive) && age > maximumChildAge) {
                setChildAgeError(true);
            } else {
                setChildAgeError(false);
            }

            if (todaysDate < accountDOBinDateFormate) {
                setNegativeError(true);
            } else {
                setNegativeError(false);
            }
        } catch {
            setChildAgeError(false);
            setNegativeError(false);
        }
    };

    function onChangeHandler(event: any) {
        if ((event.target.value.length === 2 || event.target.value.length === 5) && birthDateFieldPressedKey !== 8 && birthDateFieldPressedKey !== 46) {
            event.target.value = event.target.value + "/";
        }
        checkMemberAge(event.currentTarget.value);
    }

    return (
        <React.Fragment>
            <div id="preferences-form" aria-hidden="true">
                <form onSubmit={handleSubmit(firstStepSubmit)} id="complete-account-setup-one-form-container">
                    <fieldset className="form-row no-margin inline">
                        <div className="row">
                            <div className="col-xs-12 col-sm-4 col-no-gutter">
                                <legend className="legend-label"> <label htmlFor="firstName">
                                    Full Name{" "}
                                    <span className="required" title="Mandatory">
                                        *
                                    </span>
                                </label>  </legend>
                            </div>

                            <div className="col-xs-12 col-sm-4 col-no-gutter">
                                <div className={`form-row row`}>
                                    <div className="form-label sr-only col-xs-12 col-no-gutter">
                                        <label htmlFor="firstName">
                                            First Name{" "}
                                            <span className="required" title="Mandatory">
                                                *
                                            </span>
                                        </label>
                                    </div>
                                    <div className="form-input col-xs-12 col-no-gutter">
                                        <div className="row">
                                            <div className={`form-input row`}>
                                                <div className={`col-xs-8 col-no-gutter ${errors["firstName"] && "status-error"}`}>
                                                    <input
                                                        type="text"
                                                        className="inputbox"
                                                        name="firstName"
                                                        defaultValue={state.firstName}
                                                        id="first-name"
                                                        ref={register({ pattern: PATTERNS.FIRSTNAME.pattern, required: true })}
                                                        placeholder="First Name"
                                                        maxLength={30}
                                                    />
                                                </div>
                                                <div className="col-xs-4 col-no-gutter" style={{ paddingLeft: "0.5rem" }}>
                                                    <input
                                                        type="text"
                                                        className="inputbox"
                                                        name="middleName"
                                                        defaultValue={state.middleName}
                                                        id="middle-name"
                                                        ref={register({
                                                            required: false,
                                                            pattern: PATTERNS.MIDDLE_INITIAL.pattern,
                                                        })}
                                                        placeholder="Middle"
                                                        maxLength={1}
                                                    />
                                                </div>
                                            </div>
                                            {errors["middleName"] && errors["middleName"].type === "pattern" && (
                                                <span className="form-error">{PATTERNS.MIDDLE_INITIAL.message}</span>
                                            )}

                                            {errors["firstName"] && errors["firstName"].type === "pattern" && (
                                                <span className="form-error">{PATTERNS.FIRSTNAME.message}</span>
                                            )}
                                            {errors["firstName"] && errors["firstName"].type === "required" && (
                                                <span className="form-error">First name is required.</span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className={`form-row row ${errors["lastName"] && "status-error"}`}>
                                    <div className="form-label sr-only col-xs-12 col-no-gutter">
                                        <label htmlFor="lastName">Last Name </label>
                                    </div>
                                    <div className="form-input col-xs-12 col-no-gutter">
                                        <input
                                            type="text"
                                            defaultValue={state.lastName}
                                            name="lastName"
                                            className="inputbox"
                                            id="last-name"
                                            ref={register({ pattern: /^[a-zA-Z\s\-']+$/, required: true })}
                                            placeholder="Last Name"
                                        />
                                        {errors["lastName"] && errors["lastName"].type === "pattern" && (
                                            <span className="form-error">Please enter valid last name.</span>
                                        )}
                                        {errors["lastName"] && errors["lastName"].type === "required" && <span className="form-error">Last name is required</span>}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </fieldset>

                    {countryRequiresAuthentication &&
                    <>
                    <div className={`form-row inline row`}>
                        <div className="form-label col-xs-12 col-sm-4 col-no-gutter">
                            <label htmlFor="label-address-zipcode">
                                Social Security #{" "}
                                <span className="required" title="Mandatory">
                                    *
                                </span>
                            </label>
                        </div>
                        <div className="form-input col-xs-12 col-sm-4 col-no-gutter">
                            <div className="row">
                                <div className="form-input row">
                                    <div className={`col-xs-4 col-no-gutter ${errors["ssn1"] && "status-error"}`}>
                                        <input
                                            type="password"
                                            maxLength={3}
                                            className="inputbox"
                                            autoComplete="off"
                                            defaultValue={state.ssn1}
                                            onChange={handleSSN1}
                                            name="ssn1"
                                            id="ssn1"
                                            ref={register({ pattern: /[\d]{3}/, required: true })}
                                        />
                                    </div>
                                    <div className={`col-xs-3 col-gutter-horizontal ${errors["ssn2"] && "status-error"}`}>
                                        <input
                                            type="password"
                                            maxLength={2}
                                            className="inputbox"
                                            autoComplete="off"
                                            defaultValue={state.ssn2}
                                            onChange={handleSSN2}
                                            name="ssn2"
                                            id="ssn2"
                                            ref={register({ pattern: /[\d]{2}/, required: true })}
                                        />
                                    </div>
                                    <div className={`col-xs-5 col-no-gutter ${errors["ssn3"] && "status-error"}`}>
                                        <input
                                            type="password"
                                            maxLength={4}
                                            className="inputbox"
                                            autoComplete="off"
                                            defaultValue={state.ssn3}
                                            onChange={handleSSN3}
                                            name="ssn3"
                                            id="ssn3"
                                            ref={register({ pattern: /[\d]{4}/, required: true })}
                                        />
                                    </div>
                                    {(errors["ssn1"] || errors["ssn2"] || errors["ssn3"]) && (
                                        <span className="form-error">Please enter valid Social Security Number.</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className={`form-row inline row ${(state.confirmSNNError || errors["cssn1"] || errors["cssn2"] || errors["cssn3"]) && "status-error"}`}>
                        <div className="form-label col-xs-12 col-sm-4 col-no-gutter">
                            <label htmlFor="label-address-zipcode">
                                Confirm Social Security #{" "}
                                <span className="required" title="Mandatory">
                                    *
                                </span>
                            </label>
                        </div>
                        <div className="form-input col-xs-12 col-sm-4 col-no-gutter">
                            <div className="row">
                                <div className="form-input row">
                                    <div className="col-xs-4 col-no-gutter">
                                        <input
                                            type="password"
                                            maxLength={3}
                                            className="inputbox"
                                            autoComplete="off"
                                            defaultValue={state.cssn1}
                                            onChange={handleCSSN1}
                                            name="cssn1"
                                            id="cssn1"
                                            ref={register({ pattern: /[\d]{3}/, required: true })}
                                        />
                                    </div>
                                    <div className="col-xs-3 col-gutter-horizontal">
                                        <input
                                            type="password"
                                            maxLength={2}
                                            className="inputbox"
                                            autoComplete="off"
                                            defaultValue={state.cssn2}
                                            onChange={handleCSSN2}
                                            name="cssn2"
                                            id="cssn2"
                                            ref={register({ pattern: /[\d]{2}/, required: true })}
                                        />
                                    </div>
                                    <div className="col-xs-5 col-no-gutter">
                                        <input
                                            type="password"
                                            maxLength={4}
                                            className="inputbox"
                                            autoComplete="off"
                                            defaultValue={state.cssn3}
                                            onChange={handleCSSN3}
                                            name="cssn3"
                                            id="cssn3"
                                            ref={register({ pattern: /[\d]{4}/, required: true })}
                                        />
                                    </div>
                                </div>
                                {(errors["cssn1"] || errors["cssn2"] || errors["cssn3"]) && (
                                    <span className="form-error">Please enter valid Confirm Social Security Number.</span>
                                )}
                                {!(errors["cssn1"] || errors["cssn2"] || errors["cssn3"]) && state.confirmSNNError && (
                                    <span className="form-error">Your Social Security Numbers do not match.</span>
                                )}
                            </div>
                        </div>
                    </div>
                    </>
}

                    <div className={`form-row inline row ${errors["dob"] && "status-error"}`}>
                        <div className="form-label col-xs-12 col-sm-4 col-no-gutter">
                            <label htmlFor="label-driversLicense">
                                Date of Birth{" "}
                                <span className="required" title="Mandatory">
                                    *
                                </span>
                            </label>
                        </div>

                        <div className="form-input col-xs-12 col-sm-4 col-no-gutter">
                            <input
                                type="text"
                                name="dob"
                                defaultValue={state.dob}
                                className="inputbox"
                                id="dob"
                                onKeyDown={updatBirthDatePressedKey}
                                onChange={onChangeHandler}
                                ref={register({
                                    required: true,
                                    pattern: /^(([0][1-9]|1[012])[/]([0][1-9]|[12][0-9]|3[01])[/](18|19|20|21)?[0-9]{4})*$/,
                                    maxLength: 10,
                                })}
                                maxLength={10}
                                placeholder="MM/DD/YYYY"
                            />
                            {errors["dob"] && errors["dob"].type === "required" && <span className="form-error">Date of Birth is required.</span>}
                            {errors["dob"] && errors["dob"].type === "pattern" && (
                                <span className="form-error">Invalid format. Date of Birth should be MM/DD/YYYY.</span>
                            )}
                            {negativeAgeError && <span className="form-error">Please enter valid date. The given date is future date.</span>}
                            {childAgeError && <span className="form-error">Children must be {maximumChildAge} years old or younger.</span>}
                        </div>
                    </div>

                    <div className="margin-vertical-l"></div>
                    <div className="row between-xs">
                        <div className="col-xs col-no-gutter flex-grow-null"></div>
                        <div className="col-xs col-no-gutter flex-grow-null">
                            <ul className="list-inline list-reset">
                                <li className="margin-left-s">
                                    <button type="submit" className="btn-default btn-primary btn-min-width">
                                        <span className="btn-text">Next</span>
                                    </button>
                                </li>
                            </ul>
                        </div>
                    </div>
                </form>
            </div>
        </React.Fragment>
    );
};

export default CompleteFamilySetupStep1;
